import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase';
import { ANY_SPORT_MARKET } from '@sorare/core/src/constants/__generated__/routes';
import { useIntlContext } from '@sorare/core/src/contexts/intl';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useWalletDrawerContext } from '@sorare/core/src/contexts/walletDrawer';
import { useAmountWithConversion } from '@sorare/core/src/hooks/useAmountWithConversion';
import { useMonetaryAmount } from '@sorare/core/src/hooks/useMonetaryAmount';
import { marketCredits } from '@sorare/core/src/lib/glossary';

import { DumbNotification } from '#src/components/activity/DumbNotification/index.tsx';
import { commonNotificationInterfaceFragment } from '#src/components/activity/Notification/fragments.ts';
import type { CommonNotificationProps } from '#src/components/activity/Notification/types.ts';

import type { ConversionCreditNotification_ConversionCreditNotification } from './__generated__/index.graphql.ts';

type Props = CommonNotificationProps & {
  notification: ConversionCreditNotification_ConversionCreditNotification;
};

const WalletLink = styled(ButtonBase)`
  text-decoration: underline;
`;

export const ConversionCreditNotification = ({
  notification,
  ...rest
}: Props) => {
  const { formatDistanceToNow } = useIntlContext();
  const { generateSportContextPath } = useSportContext();
  const { toggleDrawer } = useWalletDrawerContext();
  const { toMonetaryAmount } = useMonetaryAmount();
  const { createdAt, name, sport, read, conversionCredit } = notification;
  const { main } = useAmountWithConversion({
    monetaryAmount: toMonetaryAmount(conversionCredit?.maxDiscount),
  });
  if (!conversionCredit) return null;
  const { endDate, percentageDiscount } = conversionCredit;
  const timeLeft = formatDistanceToNow(endDate, { addSuffix: false });
  return (
    <DumbNotification
      name={name}
      title={
        <FormattedMessage
          {...marketCredits.haveDiscountCredits}
          values={{
            maxDiscount: main,
            percentageDiscount,
            timeLeft,
            walletLink: (...chunks) => (
              <WalletLink
                onClick={() => {
                  toggleDrawer();
                }}
              >
                {chunks}
              </WalletLink>
            ),
          }}
        />
      }
      link={{
        pathname: generateSportContextPath(ANY_SPORT_MARKET),
      }}
      createdAt={createdAt}
      sport={sport}
      read={read}
      {...rest}
    />
  );
};

ConversionCreditNotification.fragments = {
  ConversionCreditNotification: gql`
    fragment ConversionCreditNotification_ConversionCreditNotification on ConversionCreditNotification {
      id
      name
      conversionCredit {
        id
        endDate
        percentageDiscount
        maxDiscount {
          ...useMonetaryAmount_monetaryAmount
        }
      }
      ...Notification_notificationInterface
    }
    ${commonNotificationInterfaceFragment}
    ${useMonetaryAmount.fragments.monetaryAmount}
  ` as TypedDocumentNode<ConversionCreditNotification_ConversionCreditNotification>,
};
